import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  CircularProgress,
  CardMedia,
  CardActions,
  Chip
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  CloudUpload as UploadIcon,
  Palette as DesignIcon
} from '@mui/icons-material';
import axios from 'axios';
import { AlertInfo } from '../types';
import { accountTemplateApi } from '../services/api';

interface TemplateDesign {
  id: string;
  name: string;
  description: string;
  category: string;
  preview: string;
}

interface AccountTemplateManagerProps {
  accountId: string;
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

const AccountTemplateManager: React.FC<AccountTemplateManagerProps> = ({ accountId, showAlert }) => {
  const [designs, setDesigns] = useState<TemplateDesign[]>([]);
  const [loading, setLoading] = useState(true);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [hasExistingTemplate, setHasExistingTemplate] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const [formData, setFormData] = useState({
    companyName: '',
    termsUrl: '',
    privacyUrl: '',
    baseColor: '#ffffff',
    accentColor: '#1976d2'
  });

  useEffect(() => {
    fetchDesigns();
    fetchAccountTemplate();
  }, [accountId]);




  const fetchDesigns = async () => {
    try {
      const response = await accountTemplateApi.getDesigns();
      setDesigns(response.designs || []);
    } catch (error) {
      console.error('Error fetching designs:', error);
      showAlert('Failed to fetch template designs. Please check your connection.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountTemplate = async () => {
    try {
      const response = await accountTemplateApi.getAccountTemplate(accountId);
      console.log('Fetched account template response:', response); // Debug log

      // The API returns { template: ... } directly, not nested in data
      if (response.template) {
        const template = response.template;
        console.log('Found existing template:', template); // Debug log

        setFormData({
          companyName: template.companyName || '',
          termsUrl: template.termsUrl || '',
          privacyUrl: template.privacyUrl || '',
          baseColor: template.baseColor || '#ffffff',
          accentColor: template.accentColor || '#1976d2'
        });
        setLogoUrl(template.logoUrl || '');
        setSelectedDesign(template.designType || '');
        setHasExistingTemplate(true);
        console.log('Set hasExistingTemplate to true'); // Debug log
      } else {
        console.log('No existing template found'); // Debug log
        setHasExistingTemplate(false);
      }
    } catch (error) {
      console.error('Error fetching account template:', error);
      setHasExistingTemplate(false);
    }
  };

  const handleSetupTemplate = () => {
    setSetupDialogOpen(true);
  };

  const handleSelectDesign = (designId: string) => {
    setSelectedDesign(designId);
    generatePreview(designId);
  };

  const generatePreview = async (designId: string) => {
    try {
      const response = await accountTemplateApi.generatePreview({
        designType: designId,
        logoUrl: logoUrl,
        companyName: formData.companyName || 'Sample Company',
        termsUrl: formData.termsUrl,
        privacyUrl: formData.privacyUrl,
        baseColor: formData.baseColor,
        accentColor: formData.accentColor,
        subject: 'Sample Email Subject',
        content: '<p>This is a sample email content to show how your template will look with your selected colors and branding.</p><p>Your company information and colors are applied to create a professional, branded email experience.</p>'
      });

      const blob = response;
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error generating preview:', error);
      if (axios.isAxiosError(error)) {
        showAlert(`Failed to generate preview: ${error.response?.status || 'Network Error'}`, 'error');
      } else {
        showAlert('Failed to generate preview - server may be offline', 'error');
      }
    }
  };

  const handleSaveTemplate = async () => {
    try {
      console.log('Saving template with data:', {
        accountId,
        formData,
        selectedDesign,
        logoFile: logoFile?.name
      });

      const formDataToSend = new FormData();
      formDataToSend.append('accountId', accountId);
      formDataToSend.append('companyName', formData.companyName);
      formDataToSend.append('termsUrl', formData.termsUrl);
      formDataToSend.append('privacyUrl', formData.privacyUrl);
      formDataToSend.append('baseColor', formData.baseColor);
      formDataToSend.append('accentColor', formData.accentColor);

      if (selectedDesign) {
        formDataToSend.append('designType', selectedDesign);
      }

      if (logoFile) {
        formDataToSend.append('logo', logoFile, logoFile.name);
        console.log('Including logo file in upload:', logoFile.name);
      }

      await accountTemplateApi.saveAccountTemplate(formDataToSend);

      showAlert('Template saved successfully', 'success');
      setSetupDialogOpen(false);

      // Clear the logo file since it's now saved on server
      setLogoFile(null);

      // Refresh the account template to get the updated logoUrl from server
      await fetchAccountTemplate();

      // If we have a selected design, regenerate the preview with the new data
      if (selectedDesign) {
        generatePreview(selectedDesign);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.error || `Failed to save template: ${error.response?.status || 'Network Error'}`;
        showAlert(errorMessage, 'error');
      } else {
        showAlert('Failed to save template - server may be offline', 'error');
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        showAlert('Logo file must be less than 1MB', 'error');
        return;
      }
      setLogoFile(file);
      const url = URL.createObjectURL(file);
      setLogoUrl(url);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Email Templates</Typography>
        <Box display="flex" gap={2}>
          {selectedDesign && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={async () => {
                try {
                  // Unset the design by saving template without designType
                  const formDataToSend = new FormData();
                  formDataToSend.append('accountId', accountId);
                  formDataToSend.append('companyName', formData.companyName);
                  formDataToSend.append('termsUrl', formData.termsUrl);
                  formDataToSend.append('privacyUrl', formData.privacyUrl);
                  formDataToSend.append('baseColor', formData.baseColor);
                  formDataToSend.append('accentColor', formData.accentColor);
                  // Don't include designType to unset it

                  await accountTemplateApi.saveAccountTemplate(formDataToSend);
                  setSelectedDesign('');
                  setPreviewUrl('');
                  showAlert('Design unset successfully. Emails will be sent without template wrapper.', 'success');
                  fetchAccountTemplate();
                } catch (error) {
                  console.error('Error unsetting design:', error);
                  showAlert('Failed to unset design', 'error');
                }
              }}
            >
              Unset Design
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<DesignIcon />}
            onClick={handleSetupTemplate}
          >
            {hasExistingTemplate ? 'Edit Template' : 'Setup Template'}
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" mb={3}>
        Choose a beautiful template design for your emails. Set up your company information once and use it across all your email campaigns.
      </Typography>

      {/* Current Design Section */}
      {selectedDesign && (
        <Box mb={4} p={3} bgcolor="background.paper" borderRadius={2} border="1px solid #e0e0e0">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Currently Selected Design
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip
                  label={designs.find(d => d.id === selectedDesign)?.name || selectedDesign}
                  color="primary"
                  variant="filled"
                />
                <Typography variant="body2" color="text.secondary">
                  Category: {designs.find(d => d.id === selectedDesign)?.category || 'Unknown'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={async () => {
                if (!selectedDesign) {
                  showAlert('Please select a design first', 'warning');
                  return;
                }

                try {
                  const formDataToSend = new FormData();
                  formDataToSend.append('accountId', accountId);
                  formDataToSend.append('companyName', formData.companyName);
                  formDataToSend.append('termsUrl', formData.termsUrl);
                  formDataToSend.append('privacyUrl', formData.privacyUrl);
                  formDataToSend.append('baseColor', formData.baseColor);
                  formDataToSend.append('accentColor', formData.accentColor);
                  formDataToSend.append('designType', selectedDesign);

                  if (logoFile) {
                    formDataToSend.append('logo', logoFile);
                    console.log('Saving design with logo file:', logoFile.name);
                  }

                  await accountTemplateApi.saveAccountTemplate(formDataToSend);
                  showAlert('Design saved successfully', 'success');

                  // Clear the logo file since it's now saved on server
                  setLogoFile(null);

                  // Refresh the account template to get the updated logoUrl from server
                  await fetchAccountTemplate();

                  // Regenerate the preview with the new data
                  generatePreview(selectedDesign);
                } catch (error) {
                  console.error('Error saving design:', error);
                  showAlert('Failed to save design', 'error');
                }
              }}
              disabled={!formData.companyName}
            >
              Save Design
            </Button>
          </Box>

          {/* Inline Preview */}
          {previewUrl && (
            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom>
                Preview
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  maxHeight: '400px'
                }}
              >
                <iframe
                  src={previewUrl}
                  width="100%"
                  height="400px"
                  style={{ border: 'none' }}
                  title="Email Template Preview"
                />
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Typography variant="h6" gutterBottom>
        {selectedDesign ? 'Change Design' : 'Choose a Design'}
      </Typography>

      <Grid container spacing={3}>
        {designs.length === 0 ? (
          <Grid item xs={12}>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No template designs available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please check your server connection or contact support.
              </Typography>
            </Box>
          </Grid>
        ) : (
          designs.map((design) => (
            <Grid item xs={12} md={6} lg={4} key={design.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedDesign === design.id ? '3px solid #1976d2' : '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 },
                  position: 'relative'
                }}
                onClick={() => handleSelectDesign(design.id)}
              >
                {selectedDesign === design.id && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: '#1976d2',
                      color: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      zIndex: 1
                    }}
                  >
                    ✓
                  </Box>
                )}
                <CardMedia
                  component="img"
                  height="200"
                  image={design.preview}
                  alt={design.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" gutterBottom>
                      {design.name}
                    </Typography>
                    <Chip label={design.category} size="small" color="primary" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {design.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<PreviewIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      generatePreview(design.id);
                    }}
                  >
                    Preview
                  </Button>
                  {selectedDesign === design.id && (
                    <Chip label="Selected" size="small" color="primary" variant="filled" />
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Setup Template Dialog */}
      <Dialog open={setupDialogOpen} onClose={() => setSetupDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Setup Your Email Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Provide your company information to customize your email templates. This information will be used across all your email campaigns.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    fullWidth
                  >
                    {logoFile ? logoFile.name : 'Upload Logo (Max 1MB)'}
                  </Button>
                </label>

                {/* Show current logo or uploaded logo preview */}
                {(logoUrl || logoFile) && (
                  <Box mt={2} textAlign="center">
                    <Typography variant="caption" display="block" gutterBottom>
                      {logoFile ? 'New Logo (not saved yet)' : 'Current Logo'}
                    </Typography>
                    <Box
                      component="img"
                      src={logoUrl}
                      alt="Logo preview"
                      sx={{
                        maxWidth: '100px',
                        maxHeight: '60px',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>



            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Terms of Service URL"
                value={formData.termsUrl}
                onChange={(e) => setFormData({ ...formData, termsUrl: e.target.value })}
                placeholder="https://example.com/terms"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Privacy Policy URL"
                value={formData.privacyUrl}
                onChange={(e) => setFormData({ ...formData, privacyUrl: e.target.value })}
                placeholder="https://example.com/privacy"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Base Color"
                type="color"
                value={formData.baseColor}
                onChange={(e) => setFormData({ ...formData, baseColor: e.target.value })}
                helperText="Primary background color"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Accent Color"
                type="color"
                value={formData.accentColor}
                onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                helperText="Color for buttons, links, and highlights"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveTemplate} variant="contained" disabled={!formData.companyName}>
            Save Template
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default AccountTemplateManager;